import { useEffect, useState } from "react";
import {
  Container,
  Title,
  Text,
  Card,
  Stack,
  Group,
  TextInput,
  Textarea,
  Button,
  NumberInput,
  Divider,
  FileInput,
  ActionIcon,
} from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { useForm } from "@mantine/form";
import { useNavigate, useParams } from "react-router-dom";
import { IconPlus, IconTrash } from "@tabler/icons-react";
import { useProjectSummarySheet, useUpdateProjectSummarySheet } from "../hooks/useProjectSummarySheet";
import type { UpdateProjectSummarySheetDto } from "../../../api/projectSummarySheet";
import { dhivehiInputStyle } from "../../../utils/dhivehiText";
import { autoResizeTextareaStyles, resizeTextareaToContent } from "../../../utils/textareaAutoResize";
import { RegulatoryClausePicker } from "../../../components/RegulatoryClausePicker";
import { useAuth } from "../../../auth/AuthContext";
import { canEditProjectSummarySheet } from "../permissions";

type BudgetRow = { budgetYear: string; allocatedAmount: string };

function readFileAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const r = reader.result as string;
      resolve(r.includes(",") ? r.split(",")[1] : r);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export function ProjectSummarySheetEdit() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data: sheet, isLoading } = useProjectSummarySheet(id!);
  const update = useUpdateProjectSummarySheet();
  const [file, setFile] = useState<File | null>(null);

  const form = useForm({
    initialValues: {
      summarySheetNumber: "",
      sheetDate: null as Date | null,
      projectNumber: "",
      projectName: "",
      fundingAgency: "",
      scopeOfWorkDetail: "",
      estimatedCost: "" as string | number,
      numberOfTimesAnnounced: "" as string | number,
      dateAnnounced: null as Date | null,
      numberOfEoi: "" as string | number,
      numberOfBidsSubmitted: "" as string | number,
      submittedParty: "",
      awardedParty: "",
      proposedPrice: "" as string | number,
      proposedDuration: "",
      detailsOfTheProject: "",
      bidCommitteeReference: "",
      agendaReference: "",
      budgetAllocations: [] as BudgetRow[],
    },
  });

  useEffect(() => {
    if (!sheet) return;
    form.setValues({
      summarySheetNumber: sheet.summarySheetNumber,
      sheetDate: new Date(sheet.sheetDate),
      projectNumber: sheet.projectNumber || "",
      projectName: sheet.projectName,
      fundingAgency: sheet.fundingAgency || "",
      scopeOfWorkDetail: sheet.scopeOfWorkDetail || "",
      estimatedCost: sheet.estimatedCost ?? "",
      numberOfTimesAnnounced: sheet.numberOfTimesAnnounced ?? "",
      dateAnnounced: sheet.dateAnnounced ? new Date(sheet.dateAnnounced) : null,
      numberOfEoi: sheet.numberOfEoi ?? "",
      numberOfBidsSubmitted: sheet.numberOfBidsSubmitted ?? "",
      submittedParty: sheet.submittedParty || "",
      awardedParty: sheet.awardedParty || "",
      proposedPrice: sheet.proposedPrice ?? "",
      proposedDuration: sheet.proposedDuration || "",
      detailsOfTheProject: sheet.detailsOfTheProject || "",
      bidCommitteeReference: sheet.bidCommitteeReference || "",
      agendaReference: sheet.agendaReference || "",
      budgetAllocations: (sheet.budgetAllocations || []).map((b) => ({
        budgetYear: String(b.budgetYear),
        allocatedAmount: String(b.allocatedAmount),
      })),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sheet]);

  useEffect(() => {
    const resize = () => {
      document
        .querySelectorAll(".pss-auto-resize-textarea textarea")
        .forEach((el) => resizeTextareaToContent(el as HTMLTextAreaElement));
    };
    const t = window.setTimeout(resize, 0);
    return () => window.clearTimeout(t);
  }, [form.values.scopeOfWorkDetail, form.values.detailsOfTheProject]);

  const handleSubmit = async (values: typeof form.values) => {
    const budgetAllocations: NonNullable<UpdateProjectSummarySheetDto["budgetAllocations"]> = [];
    let i = 0;
    for (const row of values.budgetAllocations) {
      const y = parseInt(String(row.budgetYear), 10);
      const amt = parseFloat(String(row.allocatedAmount));
      if (!Number.isNaN(y) && !Number.isNaN(amt)) {
        budgetAllocations.push({ budgetYear: y, allocatedAmount: amt, sortOrder: i++ });
      }
    }

    let base64File: string | undefined;
    let fileName: string | undefined;
    if (file) {
      base64File = await readFileAsBase64(file);
      fileName = file.name;
    }

    const payload: UpdateProjectSummarySheetDto = {
      summarySheetNumber: values.summarySheetNumber,
      sheetDate: (values.sheetDate || new Date()).toISOString(),
      projectNumber: values.projectNumber || null,
      projectName: values.projectName,
      fundingAgency: values.fundingAgency || null,
      scopeOfWorkDetail: values.scopeOfWorkDetail || null,
      estimatedCost:
        values.estimatedCost === "" || values.estimatedCost === undefined ? null : Number(values.estimatedCost),
      numberOfTimesAnnounced:
        values.numberOfTimesAnnounced === "" || values.numberOfTimesAnnounced === undefined
          ? null
          : Number(values.numberOfTimesAnnounced),
      dateAnnounced: values.dateAnnounced ? values.dateAnnounced.toISOString() : null,
      numberOfEoi: values.numberOfEoi === "" || values.numberOfEoi === undefined ? null : Number(values.numberOfEoi),
      numberOfBidsSubmitted:
        values.numberOfBidsSubmitted === "" || values.numberOfBidsSubmitted === undefined
          ? null
          : Number(values.numberOfBidsSubmitted),
      submittedParty: values.submittedParty || null,
      awardedParty: values.awardedParty || null,
      proposedPrice:
        values.proposedPrice === "" || values.proposedPrice === undefined ? null : Number(values.proposedPrice),
      proposedDuration: values.proposedDuration || null,
      detailsOfTheProject: values.detailsOfTheProject || null,
      bidCommitteeReference: values.bidCommitteeReference || null,
      agendaReference: values.agendaReference || null,
      budgetAllocations,
    };
    if (base64File && fileName) {
      payload.base64File = base64File;
      payload.fileName = fileName;
    }

    update.mutate(
      { id: id!, data: payload },
      { onSuccess: () => navigate(`/project-summary-sheets/${id}`) }
    );
  };

  if (isLoading || !sheet) {
    return (
      <Container py="xl">
        <Text>Loading…</Text>
      </Container>
    );
  }

  if (!canEditProjectSummarySheet(sheet, user)) {
    return (
      <Container py="xl">
        <Text>Only draft or rejected sheets can be edited, by the author or a procurement officer.</Text>
        <Button mt="md" type="button" onClick={() => navigate(`/project-summary-sheets/${id}`)}>
          Back
        </Button>
      </Container>
    );
  }

  return (
    <Container size="md" py="xl">
      <Stack gap="lg">
        <Title order={2}>Edit project summary sheet</Title>
        <Card shadow="sm" p="lg">
          <form onSubmit={form.onSubmit(handleSubmit)}>
            <Stack gap="md">
              <Group grow>
                <TextInput label="Summary sheet number" required {...form.getInputProps("summarySheetNumber")} />
                <DatePickerInput label="Sheet date" required {...form.getInputProps("sheetDate")} />
              </Group>
              <Group grow>
                <TextInput label="Project number" {...form.getInputProps("projectNumber")} />
                <TextInput
                  label="Project name"
                  required
                  dir="rtl"
                  styles={{ input: { textAlign: "right", ...dhivehiInputStyle(form.values.projectName) } }}
                  {...form.getInputProps("projectName")}
                />
              </Group>
              <TextInput
                label="Funding agency"
                dir="rtl"
                styles={{ input: { textAlign: "right", ...dhivehiInputStyle(form.values.fundingAgency) } }}
                {...form.getInputProps("fundingAgency")}
              />
              <Group justify="flex-end">
                <RegulatoryClausePicker
                  onInsert={(text) =>
                    form.setFieldValue(
                      "scopeOfWorkDetail",
                      `${form.values.scopeOfWorkDetail || ""}${text}`
                    )
                  }
                />
              </Group>
              <Textarea
                label="Scope of work (detail)"
                minRows={6}
                className="pss-auto-resize-textarea"
                dir="rtl"
                styles={{
                  input: {
                    textAlign: "right",
                    ...autoResizeTextareaStyles,
                    ...dhivehiInputStyle(form.values.scopeOfWorkDetail),
                  },
                }}
                {...form.getInputProps("scopeOfWorkDetail")}
                onInput={(e) => resizeTextareaToContent(e.currentTarget)}
              />
              <NumberInput label="Estimated cost (MVR)" min={0} decimalScale={2} thousandSeparator="," {...form.getInputProps("estimatedCost")} />
              <Divider label="Allocated budget by year" labelPosition="center" />
              {form.values.budgetAllocations.map((_, idx) => (
                <Group key={idx} align="flex-end" grow>
                  <NumberInput label="Year" {...form.getInputProps(`budgetAllocations.${idx}.budgetYear`)} />
                  <NumberInput label="Amount (MVR)" min={0} decimalScale={2} thousandSeparator="," {...form.getInputProps(`budgetAllocations.${idx}.allocatedAmount`)} />
                  <ActionIcon color="red" variant="light" onClick={() => form.removeListItem("budgetAllocations", idx)}>
                    <IconTrash size={16} />
                  </ActionIcon>
                </Group>
              ))}
              <Button variant="light" leftSection={<IconPlus size={16} />} type="button" onClick={() => form.insertListItem("budgetAllocations", { budgetYear: "", allocatedAmount: "" })}>
                Add budget year
              </Button>
              <Divider label="Procurement history" labelPosition="center" />
              <Group grow>
                <NumberInput label="Times announced" min={0} {...form.getInputProps("numberOfTimesAnnounced")} />
                <DatePickerInput label="Date announced" clearable {...form.getInputProps("dateAnnounced")} />
              </Group>
              <Group grow>
                <NumberInput label="Number of EOI" min={0} {...form.getInputProps("numberOfEoi")} />
                <NumberInput label="Bids submitted" min={0} {...form.getInputProps("numberOfBidsSubmitted")} />
              </Group>
              <Group justify="flex-end">
                <RegulatoryClausePicker
                  onInsert={(text) =>
                    form.setFieldValue(
                      "submittedParty",
                      `${form.values.submittedParty || ""}${text}`
                    )
                  }
                />
              </Group>
              <TextInput
                label="Submitted party"
                dir="rtl"
                styles={{ input: { textAlign: "right", ...dhivehiInputStyle(form.values.submittedParty) } }}
                {...form.getInputProps("submittedParty")}
              />
              <Divider label="Recommendation of award" labelPosition="center" />
              <TextInput
                label="Awarded party"
                dir="rtl"
                styles={{ input: { textAlign: "right", ...dhivehiInputStyle(form.values.awardedParty) } }}
                {...form.getInputProps("awardedParty")}
              />
              <NumberInput label="Proposed price (MVR)" min={0} decimalScale={2} thousandSeparator="," {...form.getInputProps("proposedPrice")} />
              <TextInput
                label="Proposed duration"
                dir="rtl"
                styles={{ input: { textAlign: "right", ...dhivehiInputStyle(form.values.proposedDuration) } }}
                {...form.getInputProps("proposedDuration")}
              />
              <Group justify="flex-end">
                <RegulatoryClausePicker
                  onInsert={(text) =>
                    form.setFieldValue(
                      "detailsOfTheProject",
                      `${form.values.detailsOfTheProject || ""}${text}`
                    )
                  }
                />
              </Group>
              <Textarea
                label="Details of the project"
                minRows={10}
                className="pss-auto-resize-textarea"
                dir="rtl"
                styles={{
                  input: {
                    textAlign: "right",
                    ...autoResizeTextareaStyles,
                    ...dhivehiInputStyle(form.values.detailsOfTheProject),
                  },
                }}
                {...form.getInputProps("detailsOfTheProject")}
                onInput={(e) => resizeTextareaToContent(e.currentTarget)}
              />
              <Group grow>
                <TextInput label="Bid committee ref." {...form.getInputProps("bidCommitteeReference")} />
                <TextInput label="Agenda ref." {...form.getInputProps("agendaReference")} />
              </Group>
              <FileInput label="Replace uploaded PDF (optional)" accept="application/pdf" value={file} onChange={setFile} clearable />
              <Group justify="space-between">
                <Button variant="default" type="button" onClick={() => navigate(`/project-summary-sheets/${id}`)}>
                  Cancel
                </Button>
                <Button type="submit" loading={update.isPending}>
                  Save
                </Button>
              </Group>
            </Stack>
          </form>
        </Card>
      </Stack>
    </Container>
  );
}
